/**
 * Calculs du tableau de bord.
 *
 * Toutes les fonctions travaillent sur une série *dense* : un élément par jour
 * du calendrier, `null` quand la mesure manque. Rien n'est jamais interpolé —
 * un trou reste un trou, c'est ce qui permet à la courbe de se couper et à la
 * tendance de refuser de se prononcer.
 *
 * Les constantes physiologiques portent leur source en commentaire. Ce sont des
 * choix discutables : ils doivent rester discutables à la lecture du code.
 */

export type Goal = 'loss' | 'maintain' | 'gain';
export type Sex = 'female' | 'male' | 'other';

/**
 * Morphologie, saisie à l'inscription et modifiable ensuite. Tout est nullable :
 * un compte reste utilisable sans, avec un modèle de dépense plus grossier et
 * signalé comme tel dans l'interface.
 */
export interface BodyProfile {
  sex: Sex | null;
  birthYear: number | null;
  heightCm: number | null;
  /** Repère saisi à l'inscription : tient lieu de poids tant qu'aucune pesée
   *  n'existe, ce qui évite d'estimer une dépense sur un poids de convention. */
  startWeightKg: number | null;
}

export const EMPTY_PROFILE: BodyProfile = {
  sex: null,
  birthYear: null,
  heightCm: null,
  startWeightKg: null,
};

/** Mifflin-St Jeor exige taille et âge ; le sexe n'en déplace que la constante. */
export const profileComplete = (profile: BodyProfile): boolean =>
  profile.heightCm != null && profile.birthYear != null;

export interface Entry {
  day: string;
  weight: number | null;
  steps: number | null;
  kcal: number | null;
  /**
   * Séance de renforcement de la *veille*, même convention que les pas et les
   * calories. `null` distingue « jamais renseigné » de « pas de séance » : le
   * premier ne doit pas compter comme le second dans la régression.
   */
  gym: boolean | null;
}

export interface DayRow extends Entry {
  date: Date;
}

// --- Constantes physiologiques ----------------------------------------------

/**
 * Densité énergétique d'une variation de masse corporelle : ~7700 kcal/kg
 * (Wishnofsky 1958, popularisé sous la forme « 3500 kcal par livre »).
 *
 * Valable pour lire *a posteriori* un déficit déjà observé sur quelques
 * semaines. Elle ne vaut rien pour *projeter* : la dépense baisse avec le
 * poids, si bien que la règle surestime la perte à long terme d'un facteur ~2
 * (Hall et al., Lancet 2011 ; Thomas et al., Int J Obes 2013). Ephemeris ne
 * projette jamais — il ne fait que convertir une pente déjà observée.
 */
export const KCAL_PER_KG = 7700;

/** Écart calorique quotidien correspondant à 1 kg/semaine. */
export const KCAL_PER_KG_WEEK = KCAL_PER_KG / 7; // 1100

/**
 * Coût *net* de la marche : 0,0005 kcal·kg⁻¹·m⁻¹ (équation de marche ACSM,
 * 0,1 ml O₂·kg⁻¹·m⁻¹ à ~5 kcal par litre d'O₂).
 *
 * Multiplié par le poids *et* par la distance, donc par la longueur de pas :
 * 10 000 pas ne coûtent pas la même chose à 55 kg et 1,62 m qu'à 100 kg et
 * 1,90 m — respectivement ~185 et ~395 kcal.
 */
const KCAL_PER_KG_PER_M = 0.0005;

/** Longueur de pas ≈ 0,415 × taille, en marche spontanée. */
const STRIDE_PER_HEIGHT = 0.415;
/** Foulée de repli, correspondant à une taille de ~1,73 m. */
const DEFAULT_STRIDE_M = 0.72;

/**
 * Constantes de Mifflin-St Jeor : RMR = 10·poids + 6,25·taille − 5·âge + s.
 * L'équation la plus exacte parmi les prédictives chez l'adulte, maigre comme
 * obèse (revue systématique Frankenfield et al., JADA 2005). `other` et un
 * sexe non renseigné prennent la constante intermédiaire.
 */
const MIFFLIN_OFFSET: Record<Sex | 'unknown', number> = {
  male: 5,
  female: -161,
  other: -78,
  unknown: -78,
};

/**
 * Métabolisme de repos approché quand taille ou âge manquent. ~21 kcal/kg/j
 * encadre Mifflin pour une corpulence moyenne (≈ 24 chez un homme jeune, ≈ 19
 * chez une femme de 50 ans), d'où une incertitude d'au moins ±15 % sur ce seul
 * terme — c'est précisément ce que le profil morphologique fait disparaître.
 */
const RMR_PER_KG = 21;

/**
 * Repos → dépense hors marche : activité incompressible (posture, travail,
 * agitation) ~×1,15, thermogenèse alimentaire ~×1,10.
 */
const NON_WALKING_FACTOR = 1.27;

/** Poids de repli tant qu'aucune pesée n'est connue. */
const FALLBACK_WEIGHT = 75;

/**
 * Rémanence d'une séance sur la balance, étalée sur trois pesées.
 *
 * Une séance de la veille au soir précède la pesée du lendemain matin d'une
 * douzaine d'heures. Deux mécanismes se succèdent : le glycogène resynthétisé,
 * qui fixe 3 à 4 g d'eau par gramme (Olsson & Saltin 1970) et pèse dès le
 * lendemain ; l'œdème inflammatoire, qui culmine entre 24 et 72 h et pèse donc
 * surtout le surlendemain.
 *
 * Les poids sont fixés *a priori* d'après cette physiologie plutôt qu'ajustés :
 * le noyau couvre ainsi les 72 heures en ne coûtant qu'un seul degré de
 * liberté, l'amplitude, là où trois indicateurs distincts en coûteraient trois
 * — insoutenable sur une fenêtre dont l'effectif indépendant avoisine douze.
 */
const GYM_KERNEL = [0.6, 1, 0.5];

/**
 * Plancher de bruit d'une pesée : 0,4 % du poids corporel d'un jour à l'autre
 * (variabilité euvolémique mesurée à ~0,53 % sur 9521 jours standardisés).
 * Eau, glycogène et transit — pas de la masse grasse.
 *
 * Sert de plancher à l'écart-type résiduel : sans lui, quatre pesées alignées
 * par hasard produiraient un intervalle de confiance quasi nul.
 */
const DAILY_NOISE_FRACTION = 0.004;

export interface RestingEstimate {
  value: number;
  /** `mifflin` quand taille et âge sont connus ; sinon proportionnel au poids. */
  source: 'mifflin' | 'weight';
}

/** Âge en années révolues, à l'année près — c'est la précision utile ici. */
export const ageFrom = (birthYear: number | null, now = new Date()): number | null =>
  birthYear == null ? null : now.getFullYear() - birthYear;

/** Métabolisme de repos : Mifflin-St Jeor si le profil le permet, sinon repli. */
export function restingMetabolicRate(
  weightKg: number | null,
  profile: BodyProfile,
): RestingEstimate {
  const weight = weightKg ?? FALLBACK_WEIGHT;
  const age = ageFrom(profile.birthYear);

  if (profile.heightCm == null || age == null) {
    return { value: weight * RMR_PER_KG, source: 'weight' };
  }

  const offset = MIFFLIN_OFFSET[profile.sex ?? 'unknown'];
  return {
    value: 10 * weight + 6.25 * profile.heightCm - 5 * age + offset,
    source: 'mifflin',
  };
}

/** Longueur de pas déduite de la taille, avec repli sur une foulée moyenne. */
export const strideFor = (profile: BodyProfile): number =>
  profile.heightCm == null ? DEFAULT_STRIDE_M : (profile.heightCm / 100) * STRIDE_PER_HEIGHT;

/**
 * Dépense quotidienne *prédite* à partir de la morphologie et des pas. C'est un
 * repli : dès que les apports sont saisis, `estimateEnergy` lui préfère la
 * dépense déduite des données de l'utilisateur.
 */
export function predictedExpenditure(
  steps: number | null,
  weightKg: number | null,
  profile: BodyProfile = EMPTY_PROFILE,
): number {
  const weight = weightKg ?? FALLBACK_WEIGHT;
  const resting = restingMetabolicRate(weight, profile).value;
  const metres = (steps ?? 0) * strideFor(profile);
  return resting * NON_WALKING_FACTOR + metres * KCAL_PER_KG_PER_M * weight;
}

// --- Dates ------------------------------------------------------------------

/** Date locale au format AAAA-MM-JJ, sans passer par UTC. */
export function toISODay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Construit une date *locale* : `new Date('2026-09-15')` serait en UTC. */
export function fromISODay(iso: string): Date {
  const [year = 0, month = 1, day = 1] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Nombre de journees a venir ouvertes a la saisie.
 *
 * Une ligne porte les pas, les calories et la seance de sa *veille* : le soir,
 * ceux du jour sont deja connus et n'ont nulle part ou aller sans au moins un
 * jour d'avance. Quinze plutot qu'un parce qu'un plan de repas se saisit d'un
 * coup, pour la semaine ou la quinzaine — au-dela, on ecrirait des journees
 * qu'aucun ecran n'affiche.
 *
 * Elles restent hors de tout calcul : moyennes, tendance et maintien s'arretent
 * a aujourd'hui, faute de quoi une intention se lirait comme une mesure.
 */
export const AHEAD_DAYS = 15;

export function addDays(date: Date, count: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
}

/**
 * Série dense de `from` à `to` inclus, les mesures connues placées à leur date.
 * Les composants peuvent alors indexer par position sans se soucier des trous.
 */
export function buildSeries(from: Date, to: Date, entries: Entry[]): DayRow[] {
  const byDay = new Map(entries.map((entry) => [entry.day, entry]));
  const rows: DayRow[] = [];
  for (let date = new Date(from); date <= to; date = addDays(date, 1)) {
    const day = toISODay(date);
    const entry = byDay.get(day);
    rows.push({
      date: new Date(date),
      day,
      weight: entry?.weight ?? null,
      steps: entry?.steps ?? null,
      kcal: entry?.kcal ?? null,
      gym: entry?.gym ?? null,
    });
  }
  return rows;
}

// --- Agrégats ----------------------------------------------------------------

export type Measure = 'weight' | 'steps' | 'kcal';

export function mean(values: Array<number | null | undefined>): number | null {
  const known = values.filter((value): value is number => value != null);
  if (known.length === 0) return null;
  return known.reduce((sum, value) => sum + value, 0) / known.length;
}

/** Nombre de jours renseignés pour une mesure donnée. */
export const countKnown = (rows: DayRow[], key: Measure): number =>
  rows.filter((row) => row[key] != null).length;

/**
 * Moyenne glissante *arrière* sur `window` jours. Exige au moins la moitié de
 * la fenêtre renseignée : sous ce seuil, la moyenne dirait surtout quels jours
 * manquent.
 *
 * Elle retarde structurellement le signal de (window − 1) / 2 jours, soit trois
 * jours sur sept. C'est le prix d'une valeur disponible le jour même : une
 * moyenne centrée serait plus juste mais n'existerait qu'avec trois jours de
 * retard, donc jamais pour aujourd'hui.
 */
export function movingAverage(
  rows: DayRow[],
  window: number,
  key: Measure = 'weight',
): Array<number | null> {
  const minimum = Math.ceil(window / 2);
  return rows.map((_, index) => {
    const slice: number[] = [];
    for (let i = Math.max(0, index - window + 1); i <= index; i += 1) {
      const value = rows[i]?.[key];
      if (value != null) slice.push(value);
    }
    if (slice.length < minimum) return null;
    return slice.reduce((sum, value) => sum + value, 0) / slice.length;
  });
}

/**
 * Poids lissé à `index` : moyenne 7 jours si elle existe, sinon dernière pesée
 * connue à cette date. Sert de référence partout où un poids « actuel » est
 * nécessaire sans vouloir du bruit d'une pesée isolée.
 */
export function smoothWeightAt(rows: DayRow[], index: number): number | null {
  const slice: number[] = [];
  for (let i = Math.max(0, index - 6); i <= index; i += 1) {
    const weight = rows[i]?.weight;
    if (weight != null) slice.push(weight);
  }
  if (slice.length >= 4) return slice.reduce((sum, value) => sum + value, 0) / slice.length;

  for (let i = index; i >= 0; i -= 1) {
    const weight = rows[i]?.weight;
    if (weight != null) return weight;
  }
  return null;
}

/**
 * Valeur du noyau de séance à chaque date : somme pondérée des séances des
 * trois lignes précédentes, celle du jour comprise.
 *
 * `gym` valant `null` est traité comme une absence de séance — c'est le seul
 * choix possible pour un prédicteur, mais il biaiserait l'amplitude vers zéro
 * sur l'historique antérieur à la fonctionnalité. `firstGymRecord` sert à ne
 * jamais ajuster le terme sur ces journées-là.
 */
export function gymKernel(rows: DayRow[]): number[] {
  return rows.map((_, index) =>
    GYM_KERNEL.reduce(
      (total, factor, lag) => total + (rows[index - lag]?.gym === true ? factor : 0),
      0,
    ),
  );
}

/** Index de la première journée où la case séance a été renseignée. */
export function firstGymRecord(rows: DayRow[]): number {
  return rows.findIndex((row) => row.gym != null);
}

/**
 * Chemin SVG dans un repère `width` x `height`, l'axe Y inversé. Un `null`
 * interrompt le tracé : le segment suivant repart d'un `M`, ce qui laisse
 * visible l'absence de mesure au lieu de la combler par une droite.
 */
export function linePath(
  values: Array<number | null>,
  min: number,
  max: number,
  width: number,
  height: number,
): string {
  const span = max - min || 1;
  const last = values.length - 1;
  let path = '';
  let pen = false;

  values.forEach((value, index) => {
    if (value == null) {
      pen = false;
      return;
    }
    const x = last === 0 ? 0 : (index / last) * width;
    const y = height - ((value - min) / span) * height;
    path += `${pen ? 'L' : 'M'}${x.toFixed(2)} ${y.toFixed(2)} `;
    pen = true;
  });

  return path.trim();
}

// --- Régression linéaire ------------------------------------------------------

interface Point {
  x: number;
  y: number;
  /** Valeur du noyau de séance à cette date. */
  g: number;
}

interface Fit {
  slope: number;
  intercept: number;
  /** Amplitude du terme séance, `null` quand il n'est pas ajusté. */
  gymEffect: number | null;
  n: number;
  /** Nombre de paramètres estimés : deux, ou trois avec le terme séance. */
  parameters: number;
  /** Var(pente) = σ² × ce facteur. Vaut 1/Σ(x − x̄)² sans terme séance. */
  slopeFactor: number;
  /** Idem pour l'amplitude du terme séance. */
  gymFactor: number | null;
  residualSd: number;
  meanY: number;
  residuals: number[];
}

/** Quantiles de Student à 97,5 % ; au-delà de 30 degrés de liberté, ≈ 1,96. */
const T95: Record<number, number> = {
  1: 12.706, 2: 4.303, 3: 3.182, 4: 2.776, 5: 2.571, 6: 2.447, 7: 2.365,
  8: 2.306, 9: 2.262, 10: 2.228, 11: 2.201, 12: 2.179, 13: 2.16, 14: 2.145,
  15: 2.131, 16: 2.12, 17: 2.11, 18: 2.101, 19: 2.093, 20: 2.086, 21: 2.08,
  22: 2.074, 23: 2.069, 24: 2.064, 25: 2.06, 26: 2.056, 27: 2.052, 28: 2.048,
  29: 2.045, 30: 2.042,
};

const tCritical = (df: number): number =>
  df <= 0 ? Number.POSITIVE_INFINITY : (T95[df] ?? 1.96);

/**
 * Moindres carrés sur le temps, éventuellement augmentés du terme séance.
 *
 * Les deux cas sont résolus sur variables centrées. Avec le terme séance, la
 * variance de la pente devient σ²·Sgg/(Stt·Sgg − Stg²) : elle enfle dès que le
 * rythme des séances corrèle avec le temps — commencer à s'entraîner au milieu
 * de la fenêtre, par exemple. C'est exact, et c'est ce qu'il faut : la pente et
 * l'effet séance deviennent alors difficiles à démêler, et la marge le dit.
 */
function fitTrend(points: Point[], withGym: boolean): Fit | null {
  const n = points.length;
  if (n < 3) return null;

  const meanX = points.reduce((sum, point) => sum + point.x, 0) / n;
  const meanY = points.reduce((sum, point) => sum + point.y, 0) / n;
  const meanG = points.reduce((sum, point) => sum + point.g, 0) / n;

  let stt = 0;
  let sgg = 0;
  let stg = 0;
  let sty = 0;
  let sgy = 0;
  for (const point of points) {
    const dx = point.x - meanX;
    const dg = point.g - meanG;
    const dy = point.y - meanY;
    stt += dx * dx;
    sgg += dg * dg;
    stg += dx * dg;
    sty += dx * dy;
    sgy += dg * dy;
  }
  if (stt === 0) return null;

  const determinant = stt * sgg - stg * stg;
  // Un noyau constant, ou parfaitement aligné sur le temps, rend le système
  // singulier : on retombe alors sur la régression simple.
  const usable = withGym && n >= 6 && sgg > 0 && determinant > 1e-9 * stt * sgg;

  const slope = usable ? (sgg * sty - stg * sgy) / determinant : sty / stt;
  const gymEffect = usable ? (stt * sgy - stg * sty) / determinant : null;
  const intercept = meanY - slope * meanX - (gymEffect ?? 0) * meanG;
  const parameters = usable ? 3 : 2;
  if (n <= parameters) return null;

  const predict = (point: Point) => intercept + slope * point.x + (gymEffect ?? 0) * point.g;
  const residuals = points.map((point) => point.y - predict(point));
  const rss = residuals.reduce((sum, value) => sum + value * value, 0);

  return {
    slope,
    intercept,
    gymEffect,
    n,
    parameters,
    slopeFactor: usable ? sgg / determinant : 1 / stt,
    gymFactor: usable ? stt / determinant : null,
    residualSd: Math.sqrt(rss / (n - parameters)),
    meanY,
    residuals,
  };
}

/**
 * Autocorrélation d'ordre 1 des résidus, estimée sur les seules paires de jours
 * *consécutifs* — un résidu de lundi et un de jeudi ne sont pas voisins.
 *
 * Covariance et variance sont normalisées chacune par son propre effectif :
 * diviser une somme sur les paires disponibles par une somme sur tous les
 * résidus sous-estimerait ρ proportionnellement au nombre de trous.
 */
function lag1Autocorrelation(points: Point[], residuals: number[]): number | null {
  let covariance = 0;
  let pairs = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    const here = points[i];
    const next = points[i + 1];
    const a = residuals[i];
    const b = residuals[i + 1];
    if (!here || !next || a === undefined || b === undefined) continue;
    if (next.x - here.x !== 1) continue;
    covariance += a * b;
    pairs += 1;
  }
  // Huit paires au minimum. En dessous, ρ̂ est un tirage au sort, et la
  // correction de biais ci-dessous — qui ajoute jusqu'à (1+3ρ̂)/n — suffirait à
  // le propulser au plafond. Sur une série très trouée, peu de résidus sont
  // voisins de toute façon : ne rien corriger y est le choix raisonnable.
  if (pairs < 8) return null;

  const variance = residuals.reduce((sum, value) => sum + value * value, 0) / residuals.length;
  // Moins d'un gramme d'écart-type résiduel : les pesées sont exactement
  // alignées, il n'y a rien dont estimer la corrélation. Le seuil est absolu
  // plutôt que strictement nul, sans quoi le bruit du flottant suffirait à
  // faire croire à une structure.
  if (variance < 1e-6) return null;

  const raw = covariance / pairs / variance;
  // Biais de petit échantillon du coefficient de corrélation sérielle :
  // E[r̂] ≈ ρ − (1 + 3ρ)/n (Marriott & Pope, 1954). Sans correction, ρ est
  // systématiquement sous-estimé, donc la marge trop étroite.
  const corrected = raw + (1 + 3 * raw) / residuals.length;
  // Borné à 0,8 : au-delà, l'intervalle serait multiplié par plus de trois et
  // l'effectif indépendant tomberait sous l'unité, ce qui décrit une série
  // dégénérée plutôt qu'une physiologie. Le plancher à zéro, lui, refuse de se
  // prévaloir d'une corrélation négative pour resserrer l'intervalle.
  return Math.min(0.8, Math.max(0, corrected));
}

// --- Tendance ------------------------------------------------------------------

/**
 * Fenêtres proposées. Quatorze jours est le minimum utile : avec un bruit
 * quotidien de ~0,35 kg, l'intervalle de confiance d'une pente sur cinq jours
 * dépasse ±1 kg/semaine — plus large que l'écart entre « perte trop rapide » et
 * « perte trop lente ». Vingt-huit jours ramène cette marge sous ±0,15 kg/sem,
 * seuil à partir duquel une bande de tolérance devient discriminante.
 */
export const TREND_WINDOWS = [14, 28, 56] as const;
export const TREND_WINDOW = 28;

export type Trend =
  | {
      ok: true;
      window: number;
      /** Pente ramenée à la semaine. */
      perWeek: number;
      /** Demi-largeur de l'intervalle de confiance à 95 %, en kg/semaine. */
      marginPerWeek: number;
      /** Nombre de pesées effectivement utilisées. */
      points: number;
      /** Poids lissé au terme de la fenêtre, pour ramener la pente en %/sem. */
      weight: number;
      values: Array<number | null>;
      /** Droite ajustée, évaluée aux deux bords de la fenêtre. */
      fitted: [number, number];
      /**
       * Amplitude de l'effet séance, en kg, avec sa demi-marge à 95 %.
       * `null` quand la fenêtre n'offre pas assez de contraste entre jours
       * avec et sans séance pour que le chiffre veuille dire quelque chose.
       */
      gym: { effect: number; margin: number } | null;
      /** Écart-type résiduel retenu, plancher physiologique compris. */
      residualSd: number;
      /** ρ retenu pour les résidus ; 0 quand il n'est pas estimable. */
      autocorrelation: number;
      /** Facteur ≥ 1 appliqué à la marge du fait de cette corrélation. */
      inflation: number;
      /** Effectif *indépendant* équivalent, après correction. */
      effectiveN: number;
    }
  | { ok: false; why: string };

/**
 * Pente du poids sur `window` jours se terminant à `index`, par régression
 * linéaire, ramenée à une variation hebdomadaire et assortie de son intervalle
 * de confiance.
 *
 * Les trous ne sont pas un cas particulier : la régression travaille sur les
 * abscisses réelles, un jour manquant réduit simplement l'étalement des
 * abscisses, donc la précision — ce que la marge affiche. Seul un nombre
 * minimal de points reste exigé, en deçà duquel la variance résiduelle n'est
 * pas estimable.
 *
 * Les résidus ne sont pas indépendants : une rétention d'eau dure plusieurs
 * jours, si bien que deux écarts consécutifs à la droite se ressemblent. Les
 * moindres carrés ordinaires supposent le contraire et annoncent donc une
 * précision qu'ils n'ont pas. On estime l'autocorrélation d'ordre 1 des résidus
 * et on en déduit un effectif indépendant équivalent
 *
 *     n_eff = n (1 − ρ) / (1 + ρ)
 *
 * qui élargit la marge d'un facteur √((1 + ρ)/(1 − ρ)) et réduit les degrés de
 * liberté d'autant. C'est la correction usuelle des tests de tendance sur séries
 * corrélées (Santer et al., JGR 2000). Avec ρ ≈ 0,4 — ordre de grandeur courant
 * sur des pesées quotidiennes — vingt-huit pesées n'en valent que douze.
 */
export function trendAt(rows: DayRow[], index: number, window = TREND_WINDOW): Trend {
  if (index < 0 || index >= rows.length) {
    return { ok: false, why: 'Aucune journée sélectionnée.' };
  }
  const start = index - window + 1;
  if (start < 0) {
    return { ok: false, why: `Moins de ${window} jours d’historique avant cette date.` };
  }

  const slice = rows.slice(start, index + 1);
  // Le noyau est calculé sur la série entière : les séances des deux jours
  // précédant la fenêtre pèsent encore sur ses premières pesées.
  const kernel = gymKernel(rows);
  const points: Point[] = slice
    .map((row, offset) => ({ x: offset, y: row.weight, g: kernel[start + offset] ?? 0 }))
    .filter((point): point is Point => point.y != null);

  const minimum = Math.max(4, Math.ceil(window / 4));
  if (points.length < minimum) {
    return {
      ok: false,
      why: `${points.length} pesée${points.length > 1 ? 's' : ''} sur ${window} jours : il en faut ${minimum} pour estimer une pente et son incertitude.`,
    };
  }

  // Le terme séance n'est ajusté que sur une fenêtre entièrement postérieure à
  // la première journée renseignée : avant, `null` serait lu comme « pas de
  // séance » et écraserait l'amplitude vers zéro. Il lui faut par ailleurs du
  // contraste des deux côtés — des jours avec rémanence et des jours sans.
  const recorded = firstGymRecord(rows);
  const withGym = recorded >= 0 && start >= recorded && hasGymContrast(points);

  const fit = fitTrend(points, withGym);
  if (!fit) return { ok: false, why: 'Pesées trop rapprochées pour dégager une pente.' };

  // Plancher physiologique : même des points parfaitement alignés ne prouvent
  // pas une pente au kilo près, le bruit quotidien est irréductible.
  const residual = Math.max(fit.residualSd, DAILY_NOISE_FRACTION * fit.meanY);

  const rho = lag1Autocorrelation(points, fit.residuals) ?? 0;
  const inflation = Math.sqrt((1 + rho) / (1 - rho));
  const effectiveN = (fit.n * (1 - rho)) / (1 + rho);
  const degreesOfFreedom = Math.max(1, Math.round(effectiveN) - fit.parameters);
  const critical = tCritical(degreesOfFreedom);
  const margin = (factor: number) => critical * residual * Math.sqrt(factor) * inflation;

  // La droite tracée ignore le terme séance : c'est la tendance nette, celle
  // que le chiffre annonce, et non le tracé bosselé des pesées.
  const at = (x: number) => fit.intercept + fit.slope * x + (fit.gymEffect ?? 0) * meanKernel(points);

  return {
    ok: true,
    window,
    perWeek: fit.slope * 7,
    marginPerWeek: margin(fit.slopeFactor) * 7,
    points: points.length,
    weight: smoothWeightAt(rows, index) ?? fit.meanY,
    values: slice.map((row) => row.weight),
    fitted: [at(0), at(window - 1)],
    gym: describeGym(fit, margin),
    residualSd: residual,
    autocorrelation: rho,
    inflation,
    effectiveN,
  };
}

/**
 * Y a-t-il de quoi estimer une amplitude ? Exiger des journées à noyau *nul*
 * serait le critère évident et le mauvais : à trois séances par semaine, aucun
 * jour n'échappe aux 72 heures de rémanence, et le terme ne serait jamais
 * ajusté pour la fréquence d'entraînement la plus répandue. Ce qui compte est
 * la dispersion du noyau, pas sa nullité — d'où un seuil sur l'amplitude
 * observée et sur la répartition de part et d'autre de son milieu.
 */
function hasGymContrast(points: Point[]): boolean {
  const values = points.map((point) => point.g);
  const low = Math.min(...values);
  const high = Math.max(...values);
  // Moins d'un demi-cran d'écart : les journées se ressemblent trop.
  if (high - low < 0.5) return false;

  const middle = (low + high) / 2;
  const below = values.filter((value) => value <= middle).length;
  return below >= 4 && values.length - below >= 4;
}

/**
 * Amplitude d'une séance au-delà de laquelle l'intervalle ne dit plus rien :
 * une rétention d'eau post-entraînement dépasse rarement 1,5 kg, si bien qu'une
 * marge d'un kilo couvre déjà presque tout le plausible. Publier « +0,8 ± 33 »
 * reviendrait à afficher du bruit avec trois décimales de sérieux.
 */
const GYM_MARGIN_LIMIT = 1;

function describeGym(
  fit: Fit,
  margin: (factor: number) => number,
): { effect: number; margin: number } | null {
  if (fit.gymEffect == null || fit.gymFactor == null) return null;
  const half = margin(fit.gymFactor);
  return half > GYM_MARGIN_LIMIT ? null : { effect: fit.gymEffect, margin: half };
}

/** Noyau moyen de la fenêtre : la droite est tracée à séance « moyenne ». */
const meanKernel = (points: Point[]): number =>
  points.length === 0 ? 0 : points.reduce((sum, point) => sum + point.g, 0) / points.length;

/**
 * Horizon au-delà duquel on cesse de promettre quoi que ce soit : à six mois,
 * ni le bruit de la balance ni le rythme de vie ne ressemblent plus à ceux
 * d'aujourd'hui.
 */
const RESOLUTION_HORIZON = 180;

export interface Resolution {
  /** Jours de pesée quotidienne restant à accumuler ; 0 = c'est déjà le cas. */
  days: number;
  /** Fenêtre qui y parviendra, pas nécessairement celle affichée. */
  window: number;
}

/**
 * Combien de jours de pesée quotidienne avant que la bande puisse trancher.
 *
 * « Trop incertain pour trancher » est une impasse si l'on ignore s'il faut
 * revenir demain ou dans six semaines. La réponse se calcule : on cherche la
 * première fenêtre, et la première date, où la demi-marge passe sous la
 * demi-largeur de la bande — seuil à partir duquel le verdict ne peut plus
 * être « indéterminé », où que tombe l'estimation.
 *
 * Deux leviers agissent, et la simulation les mélange comme la réalité : boucher
 * les trous de la fenêtre courante, et atteindre une fenêtre plus longue. Les
 * pesées passées sont prises telles quelles, les futures supposées quotidiennes,
 * le bruit et l'autocorrélation supposés inchangés.
 */
export function daysToResolve(
  rows: DayRow[],
  index: number,
  band: GoalBand,
  trend: Extract<Trend, { ok: true }>,
  windows: readonly number[] = TREND_WINDOWS,
): Resolution | null {
  const halfBand = (band.high - band.low) / 2;
  if (halfBand <= 0) return null;

  const inflation = trend.inflation;
  const rho = trend.autocorrelation;

  for (let ahead = 0; ahead <= RESOLUTION_HORIZON; ahead += 1) {
    for (const window of [...windows].sort((a, b) => a - b)) {
      const last = index + ahead;
      const first = last - window + 1;
      if (first < 0) continue;

      // Abscisses des pesées disponibles : réelles dans le passé, supposées
      // quotidiennes à partir de demain.
      let count = 0;
      let sumX = 0;
      let sumXX = 0;
      for (let absolute = first; absolute <= last; absolute += 1) {
        const present = absolute > index ? true : rows[absolute]?.weight != null;
        if (!present) continue;
        const x = absolute - first;
        count += 1;
        sumX += x;
        sumXX += x * x;
      }
      const minimum = Math.max(4, Math.ceil(window / 4));
      if (count < minimum) continue;

      const sxx = sumXX - (sumX * sumX) / count;
      if (sxx <= 0) continue;

      const effectiveN = (count * (1 - rho)) / (1 + rho);
      const degreesOfFreedom = Math.max(1, Math.round(effectiveN) - 2);
      const margin =
        tCritical(degreesOfFreedom) * (trend.residualSd / Math.sqrt(sxx)) * inflation * 7;

      if (margin <= halfBand) return { days: ahead, window };
    }
  }

  return null;
}

// --- Objectifs -----------------------------------------------------------------

export interface GoalDefinition {
  /** Cible et bande exprimées en fraction du poids corporel par semaine. */
  target: number;
  low: number;
  high: number;
}

/**
 * Bandes exprimées en *pourcentage du poids corporel*, pas en kilos absolus :
 * −0,5 kg/semaine représente 0,4 %/sem à 120 kg et 0,9 %/sem à 55 kg, deux
 * régimes sans rapport.
 *
 * Perte — Garthe et al. (IJSNEM 2011) : 0,7 %/sem préserve voire augmente la
 * masse maigre là où 1,4 %/sem la stagne. Bande 0,25 à 1,0 %/sem, bornes
 * usuelles des recommandations fondées sur les preuves.
 *
 * Prise — 0,25 à 0,5 %/sem, au-delà desquels la prise devient majoritairement
 * grasse (recommandations pour athlètes naturels, Helms et al. 2014).
 */
const GOALS: Record<Goal, GoalDefinition> = {
  loss: { target: -0.007, low: -0.01, high: -0.0025 },
  maintain: { target: 0, low: -0.0025, high: 0.0025 },
  gain: { target: 0.003, low: 0.0015, high: 0.005 },
};

export const goalDefinition = (goal: Goal): GoalDefinition => GOALS[goal] ?? GOALS.loss;

export const GOAL_LABELS: Record<Goal, string> = {
  loss: 'Perte',
  maintain: 'Maintien',
  gain: 'Prise',
};

/** Bande de l'objectif convertie en kg/semaine pour un poids donné. */
export interface GoalBand {
  target: number;
  low: number;
  high: number;
  /** La même chose en fraction du poids, pour l'affichage. */
  definition: GoalDefinition;
  weight: number;
}

export function goalBand(goal: Goal, weightKg: number | null): GoalBand {
  const definition = goalDefinition(goal);
  const weight = weightKg ?? FALLBACK_WEIGHT;
  return {
    target: definition.target * weight,
    low: definition.low * weight,
    high: definition.high * weight,
    definition,
    weight,
  };
}

export type VerdictKind = 'in' | 'out' | 'unknown';

export interface Verdict {
  kind: VerdictKind;
  text: string;
  color: string;
}

/**
 * Trois états et non deux. Une pente n'est « hors cible » que si son intervalle
 * de confiance permet de le dire : quand la marge couvre toute la bande et en
 * déborde des deux côtés, la mesure ne tranche pas, et l'annoncer vaut mieux
 * que colorer au hasard.
 */
export function verdict(perWeek: number, band: GoalBand, goal: Goal, margin = 0): Verdict {
  if (margin > 0 && perWeek - margin < band.low && perWeek + margin > band.high) {
    return { kind: 'unknown', text: 'Trop incertain pour trancher', color: 'var(--ink3)' };
  }

  if (perWeek >= band.low && perWeek <= band.high) {
    return { kind: 'in', text: 'Dans la cible', color: 'var(--s)' };
  }

  const above = perWeek > band.high;
  const messages: Record<Goal, string> = {
    loss: above ? 'Perte trop lente' : 'Perte trop rapide',
    maintain: above ? 'En hausse hors bande' : 'En baisse hors bande',
    gain: above ? 'Prise trop rapide' : 'Prise trop lente',
  };
  return { kind: 'out', text: messages[goal], color: 'var(--kt)' };
}

// --- Dépense énergétique --------------------------------------------------------

/**
 * Fenêtre du bilan énergétique. Quatre semaines lissent assez de variation de
 * glycogène et d'hydratation pour que le terme de variation de poids cesse de
 * dominer l'estimation.
 */
export const ENERGY_WINDOW = 28;

export interface EnergyEstimate {
  /** Dépense quotidienne retenue, kcal/j. */
  tdee: number;
  /** `observed` : déduite des saisies. `predicted` : modèle, faute de données. */
  source: 'observed' | 'predicted';
  /** Demi-largeur de l'IC 95 %, propagée depuis la pente. `null` si modèle. */
  margin: number | null;
  /** Apport moyen déclaré sur la fenêtre. */
  intake: number | null;
  /** Dépense prédite par le modèle poids + pas, toujours calculée. */
  predicted: number;
  /**
   * `tdee / predicted` quand les deux existent. En dessous de 1, les saisies
   * sont sous-déclarées *ou* le modèle surestime la personne — trois mesures ne
   * permettent pas de trancher, et c'est sans importance : la valeur observée
   * reste la bonne référence, puisqu'elle est calibrée sur la balance et sur la
   * façon de compter de l'utilisateur.
   */
  consistency: number | null;
  window: number;
  /** Jours d'apports renseignés dans la fenêtre. */
  days: number;
  /** Sur quoi repose `predicted` : Mifflin-St Jeor ou le repli au poids seul. */
  restingSource: 'mifflin' | 'weight';
}

/**
 * Dépense quotidienne par bilan énergétique : apport moyen observé, corrigé de
 * la variation des réserves.
 *
 *     TDEE ≈ apport moyen − (pente en kg/j × 7700)
 *
 * C'est la méthode « intake-balance », validée contre l'eau doublement marquée
 * (Sanghvi, Redman, Martin, Ravussin & Hall, AJCN 2015). Elle absorbe par
 * construction le biais de déclaration : si l'utilisateur sous-estime ses
 * apports de 20 %, la dépense déduite l'est d'autant, et l'ajustement qu'on lui
 * conseille reste juste *dans son unité de mesure à lui*.
 *
 * Trois chiffres saisis suffisent, et c'est la seule estimation de dépense que
 * ces trois chiffres autorisent honnêtement.
 */
export function estimateEnergy(
  rows: DayRow[],
  index: number,
  profile: BodyProfile = EMPTY_PROFILE,
  window = ENERGY_WINDOW,
): EnergyEstimate {
  const start = Math.max(0, index - window + 1);
  const slice = rows.slice(start, index + 1);
  // À défaut de pesée, le repère de l'inscription : il vaut toujours mieux
  // qu'un poids de convention.
  const weight = smoothWeightAt(rows, index) ?? profile.startWeightKg;
  const steps = mean(slice.map((row) => row.steps));
  const predicted = predictedExpenditure(steps, weight, profile);
  const restingSource = restingMetabolicRate(weight, profile).source;

  const intakeDays = slice.filter((row) => row.kcal != null).length;
  const intake = mean(slice.map((row) => row.kcal));
  const trend = trendAt(rows, index, window);

  // Au moins la moitié des jours renseignés : sur une fenêtre trouée, la
  // moyenne des apports décrit surtout les jours où l'on a pensé à saisir.
  const usable = intake != null && intakeDays >= Math.ceil(window / 2) && trend.ok;

  if (!usable || !trend.ok || intake == null) {
    return {
      tdee: predicted,
      source: 'predicted',
      margin: null,
      intake,
      predicted,
      consistency: null,
      window,
      days: intakeDays,
      restingSource,
    };
  }

  const tdee = intake - (trend.perWeek / 7) * KCAL_PER_KG;
  return {
    tdee,
    source: 'observed',
    margin: (trend.marginPerWeek / 7) * KCAL_PER_KG,
    intake,
    predicted,
    consistency: predicted > 0 ? tdee / predicted : null,
    window,
    days: intakeDays,
    restingSource,
  };
}

// --- Bilan hebdomadaire ----------------------------------------------------------

export interface WeekSummary {
  key: string;
  from: Date;
  to: Date;
  /** Moyenne des pesées de la semaine, `null` sous quatre jours renseignés. */
  weight: number | null;
  /** Écart entre la moyenne de la semaine et celle de la précédente. */
  delta: number | null;
  days: number;
  steps: number | null;
  kcal: number | null;
}

/** Moyenne de la semaine, refusée sous quatre pesées comme la moyenne glissante. */
function weekWeight(chunk: DayRow[]): { value: number | null; days: number } {
  const weights = chunk
    .map((row) => row.weight)
    .filter((weight): weight is number => weight != null);
  return { value: weights.length >= 4 ? mean(weights) : null, days: weights.length };
}

/**
 * Découpe les `count` dernières semaines de sept jours en remontant depuis la
 * fin de la série.
 *
 * `delta` compare deux moyennes hebdomadaires et non deux pesées. Deux raisons.
 * Le bruit d'abord : une différence de moyennes sur sept jours divise l'erreur
 * par ~√7. Le rythme hebdomadaire ensuite — le poids culmine dimanche-lundi et
 * creuse le vendredi (Orsama et al., Obes Facts 2014) — si bien qu'un écart
 * entre première et dernière pesée *connues* mesure surtout quels jours de la
 * semaine ont été saisis. Un bloc de sept jours contient chaque jour de la
 * semaine une fois : le rythme s'annule.
 */
export function weeklySummaries(rows: DayRow[], count = 6): WeekSummary[] {
  const weeks: WeekSummary[] = [];

  // Une semaine de plus que demandé, uniquement pour fournir le delta de la
  // plus ancienne semaine affichée.
  for (let index = 0; index < count + 1; index += 1) {
    const end = rows.length - 7 * index;
    const start = end - 7;
    if (start < 0) break;

    const chunk = rows.slice(start, end);
    const first = chunk[0];
    const last = chunk[chunk.length - 1];
    if (!first || !last) break;

    const { value, days } = weekWeight(chunk);

    weeks.push({
      key: first.day,
      from: first.date,
      to: last.date,
      weight: value,
      delta: null,
      days,
      steps: mean(chunk.map((row) => row.steps)),
      kcal: mean(chunk.map((row) => row.kcal)),
    });
  }

  // `weeks` remonte le temps : la semaine précédente est à l'index suivant.
  for (let index = 0; index < weeks.length; index += 1) {
    const week = weeks[index];
    const previous = weeks[index + 1];
    if (week && previous && week.weight != null && previous.weight != null) {
      week.delta = week.weight - previous.weight;
    }
  }

  return weeks.slice(0, count);
}
